import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph} from '@/components/ui/icons'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {useResponsablesRoster} from '@/hooks/empresa/identidad/use-employees'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

export type PersonPickerValue = {employee_id?: string}
type PersonOption = {
  id: string
  name: string
  last_name: string
  cedula: string
  vinculo?: string
}

export function PersonPicker({
  businessId,
  value,
  onChange,
  testId,
  valueLabel,
}: {
  businessId: string
  value: PersonPickerValue
  onChange: (v: PersonPickerValue) => void
  testId: string
  /** Nombre del valor ya guardado: la nómina no se pide hasta abrir el combobox. */
  valueLabel?: string | null
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  // Aparte de la nómina, que vuelve a `undefined` al cerrar el combobox (KAI-141).
  const [lastPicked, setLastPicked] = useState<PersonOption | null>(null)
  // Solo con el combobox abierto, para no cargarle la nómina a la carga de la página.
  const {data: employees} = useResponsablesRoster(open ? businessId : undefined, open)
  const [query, setQuery] = useState('')
  const normalizedQuery = normalize(query)

  const empOptions = useMemo(
    (): PersonOption[] =>
      (employees ?? []).map((emp) => ({
        id: emp.id,
        name: emp.name,
        last_name: emp.last_name,
        cedula: emp.cedula,
        vinculo: emp.vinculo,
      })),
    [employees]
  )
  const empMatches = empOptions.filter((p) =>
    normalize(`${p.name} ${p.last_name} ${p.cedula}`).includes(normalizedQuery)
  )

  const selected: PersonOption | null =
    value.employee_id != null
      ? (empOptions.find((e) => e.id === value.employee_id) ??
        (lastPicked?.id === value.employee_id ? lastPicked : null))
      : null
  const selectedLabel = selected
    ? `${selected.name} ${selected.last_name}`
    : value.employee_id != null
      ? valueLabel
      : null

  function pick(person: PersonOption) {
    setLastPicked(person)
    onChange({employee_id: person.id})
    setOpen(false)
    setQuery('')
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <SearchComboboxShell
      title={t('investigacion.equipo.elegirPersona')}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-sm text-foreground transition-colors hover:border-[color-mix(in_oklch,var(--input),var(--foreground)_18%)] md:h-9">
          {selectedLabel ? (
            <span className="min-w-0 truncate">{selectedLabel}</span>
          ) : (
            <span className="min-w-0 truncate text-muted-foreground">{t('investigacion.equipo.elegirPersona')}</span>
          )}
          <ChevronGlyph size={14} className="opacity-60" />
        </button>
      }>
      <Input
        maxLength={tope('BUSCA')}
        placeholder={t('investigacion.equipo.buscarPersona')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        autoFocus
        data-testid={`${testId}-search`}
        className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
      />
      <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
        {empMatches.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              data-testid={`${testId}-option`}
              className="flex w-full items-baseline gap-2 rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
              onClick={() => pick(p)}>
              <span className="min-w-0 truncate">
                {p.name} {p.last_name}
              </span>
              <span className="shrink-0 font-mono text-xs text-muted-foreground">{p.cedula}</span>
              {p.vinculo === 'externo' && (
                <span className="ml-auto shrink-0">
                  <Tag tone="neutral">{t('plan.externoTag')}</Tag>
                </span>
              )}
            </button>
          </li>
        ))}
        {empMatches.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('investigacion.equipo.sinResultados')}</li>
        )}
      </ul>
    </SearchComboboxShell>
  )
}
